import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { CustomerStatus } from "@/lib/customer-status";
import type { DBCustomerStatusRow } from "@/lib/db-types";

/** Status for én kunde, fra viewet customer_status_v. */
export function useCustomerStatus(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-status", customerId],
    queryFn: async (): Promise<DBCustomerStatusRow | null> => {
      const { data, error } = await supabase
        .from("customer_status_v")
        .select("*")
        .eq("customer_id", customerId!)
        .maybeSingle();
      if (error) throw error;
      return (data as DBCustomerStatusRow | null) ?? null;
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Hele viewet (kundelisten og køen). Ubundet, derfor fetchAllPaginated. */
export function useCustomerStatuses(options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ["customer-status"],
    queryFn: () =>
      fetchAllPaginated<DBCustomerStatusRow>("customer_status_v", "*", undefined, "customer_id"),
    staleTime: 5 * 60 * 1000,
  });
}

/** RLS (customers_employee_update) avviser uten feil: 0 oppdaterte rader betyr manglende tilgang. */
function assertRowUpdated(data: unknown[] | null) {
  if (!data || data.length === 0) {
    throw new Error("Du har ikke tilgang til å endre denne kunden (bare ansatte kan endre kunder)");
  }
}

interface OverrideInput {
  customerId: string;
  status: CustomerStatus;
  reason: string;
  /** YYYY-MM-DD; tom = ingen utløpsdato. */
  until?: string | null;
}

/**
 * Overstyrer kundens status. Begrunnelse er påkrevd. `status_override_by` er
 * den faktisk innloggede (audit-triggeren bruker den som aktør-fallback).
 */
export function useSetCustomerStatusOverride() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  return useMutation({
    mutationFn: async (input: OverrideInput) => {
      const reason = input.reason.trim();
      if (!reason) throw new Error("Begrunnelse mangler");
      const { data, error } = await supabase
        .from("customers")
        .update({
          status_override: input.status,
          status_override_reason: reason,
          status_override_until: input.until || null,
          status_override_by: authUser?.id ?? null,
          status_override_at: new Date().toISOString(),
        })
        .eq("id", input.customerId)
        .select("id");
      if (error) throw error;
      assertRowUpdated(data);
    },
    onSuccess: async (_d, input) => {
      await invalidateFor(qc, "customers", { customerId: input.customerId });
    },
    onError: (err: Error) => toast.error(`Kunne ikke overstyre status: ${err.message}`),
  });
}

export function useClearCustomerStatusOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data, error } = await supabase
        .from("customers")
        .update({
          status_override: null,
          status_override_reason: null,
          status_override_until: null,
          status_override_by: null,
          status_override_at: null,
        })
        .eq("id", customerId)
        .select("id");
      if (error) throw error;
      assertRowUpdated(data);
      return customerId;
    },
    onSuccess: async (customerId) => {
      await invalidateFor(qc, "customers", { customerId });
    },
    onError: (err: Error) => toast.error(`Kunne ikke fjerne overstyringen: ${err.message}`),
  });
}
